import { useState, useEffect, useRef } from 'react';

/**
 * Fetch a list of organizations and track loading state. Re-fetches whenever
 * `limit` changes. A cancelled flag prevents stale in-flight responses from
 * clobbering state when limit changes rapidly (e.g. live limit edits in the
 * CMS preview).
 *
 * @param {() => Promise<{organizations: object[]}>} getFn - Called on each
 *   effect run; captures whatever params the caller needs via closure.
 * @param {number} limit - Re-fetch trigger; re-runs the effect when it changes.
 * @returns {{ organizations: object[], loading: boolean }}
 */
function useOrgFetcher(getFn, limit) {
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);

    const getFnRef = useRef(getFn);
    getFnRef.current = getFn;

    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        getFnRef
            .current()
            .then((res) => {
                if (!cancelled) setOrganizations(res.organizations || []);
            })
            .catch(() => {
                if (!cancelled) setOrganizations([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [limit]);

    return { organizations, loading };
}

export default useOrgFetcher;
